/* eslint no-unused-expressions: ["error", { "allowShortCircuit": true }] */
import useResizeObserver from '@react-hook/resize-observer';
import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styles from './typography.module.scss';
import { getAriaProps, getTooltipProps, variantToLineHeightMapping, } from './typography-utils';
/** Mapping variant wuth tag & styles */
export const defaultVariantMapping = {
    hero: { type: 'h1', style: 'hero' },
    h1: { type: 'h1', style: 'h1' },
    h2: { type: 'h2', style: 'h2' },
    h3: { type: 'h3', style: 'h3' },
    h4: { type: 'h4', style: 'h4' },
    h5: { type: 'h5', style: 'h5' },
    h6: { type: 'h6', style: 'h6' },
    p: { type: 'p', style: 'p' },
    'p-bold': { type: 'p', style: 'p-bold' },
    'p-small': { type: 'p', style: 'p-small' },
    'p-small-bold': { type: 'p', style: 'p-small-bold' },
    'p-large': { type: 'p', style: 'p-large' },
    'p-large-bold': { type: 'p', style: 'p-large-bold' },
    'headline-large': { type: 'h1', style: 'headline-large' },
    'page-title': { type: 'h2', style: 'page-title' },
    'modal-title': { type: 'h3', style: 'modal-title' },
    'section-label': { type: 'h4', style: 'section-label' },
    'content-label': { type: 'h5', style: 'content-label' },
    'content-label-subhead': { type: 'h6', style: 'content-label-subhead' },
    'body-large': { type: 'p', style: 'body-large' },
    'body-normal': { type: 'p', style: 'body-normal' },
    footnote: { type: 'p', style: 'footnote' },
    caption: { type: 'p', style: 'caption' },
    overline: { type: 'p', style: 'overline' },
};
/** Colors Mapping */
export var Colors;
(function (Colors) {
    Colors["base"] = "base";
    Colors["gray"] = "gray";
    Colors["grayLight"] = "grayLight";
    Colors["accent"] = "accent";
    Colors["warning"] = "warning";
    Colors["success"] = "success";
    Colors["failure"] = "failure";
    Colors["white"] = "white";
})(Colors || (Colors = {}));
export const Typography = ({ variant, children, color = Colors.base, overrideTag = null, className: additionalClass = '', role = '', ellipsis, preserveWhitespace = true, noMargin = false, tooltipConfig, wrapContent = false, dataTestId, inlineStyle = {}, ...restProps }) => {
    const isEllipsisFeatureEnabled = (ellipsis === null || ellipsis === void 0 ? void 0 : ellipsis.rows) !== undefined;
    const isEllipsisOnFirstLine = (ellipsis === null || ellipsis === void 0 ? void 0 : ellipsis.rows) === 1;
    const [contentRef, setContentRef] = useState(null);
    const [doChildrenContainNonTextValues, setDoChildrenContainNonTextValues,] = useState(true);
    // Used for scenarios like show tooltip only if ellipsis is shown.
    const [isTextContentOverflowing, setIsTextContentOverflowing] = useState(isEllipsisFeatureEnabled);
    /**
     * - The conditional execution of hooks here is fully intentional.
     * - The alternative to this is to split the component which can be a
     *     little risky for a very commonly used component like this one.
     * - Also, `ellipsis` is a feature that would need to be toggled only in an
     *     extremely rare scenario.
     *   - When needed, the `key` prop can be used to handle the situation.
     *   - The `row` setting of the config is watched and can still be updated.
     * - Without the conditional hook implementation, every instance would have
     *     observation set up regardless of whether they need it.
     *   - Not acceptable at all as it would affect the performance.
     */
    if (isEllipsisFeatureEnabled) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            /**
             * String templates in JSX separated by template literals `{}` or
             *   a new line may still be just a string but in the form of an array.
             */
            if (_.isArray(children)) {
                if (children.some((child) => !_.isString(child))) {
                    setDoChildrenContainNonTextValues(true);
                }
                else {
                    setDoChildrenContainNonTextValues(false);
                }
            }
            else if (_.isString(children)) {
                setDoChildrenContainNonTextValues(false);
            }
            else {
                setDoChildrenContainNonTextValues(true);
            }
        }, [children]);
        // eslint-disable-next-line react-hooks/rules-of-hooks, react-hooks/exhaustive-deps
        const checkIfTextContentIsOverflowing = useCallback(() => {
            /**
             * Evaluate overflow state if `ref` is available.
             * If not, fallback to presuming that the content is overflowing.
             * - If we can't evaluate overflow state, it's safe to presume and
             *     allow scenarios like show a tooltip.
             * - This would only be a temporary state until `ref` is set.
             */
            if (contentRef === null) {
                return setIsTextContentOverflowing(true);
            }
            /**
             * If `children` is components with custom tags and styling,
             *   overflow detection would not be accurate.
             * The max height wouldn't work if `children` has elements with
             *   custom styling or tags that have more height.
             * The scroll height/width wouldn't work either if `children` has custom
             *   tags that change up the expected layout.
             * The risk here is higher of presuming that content is not
             *   overflowing and not showing the tooltip.
             *
             * If `children` contains only text values, evauate overflow state.
             * Else, once again fallback to presuming that the content is overflowing.
             */
            if (doChildrenContainNonTextValues) {
                return setIsTextContentOverflowing(true);
            }
            const lineHeight = variantToLineHeightMapping[variant];
            const maxHeight = lineHeight * ellipsis.rows;
            const isOverflowing = 
            // In single-line ellipsis, overflow is hidden and wrapping is not allowed.
            // So, height checks are redundant.
            (isEllipsisOnFirstLine &&
                contentRef.offsetWidth < contentRef.scrollWidth) ||
                (isEllipsisFeatureEnabled &&
                    !isEllipsisOnFirstLine &&
                    (maxHeight < contentRef.offsetHeight ||
                        contentRef.offsetHeight < contentRef.scrollHeight));
            if (isOverflowing) {
                return setIsTextContentOverflowing(true);
            }
            return setIsTextContentOverflowing(false);
        }, [
            contentRef,
            doChildrenContainNonTextValues,
            variant,
            ellipsis.rows,
            isEllipsisOnFirstLine,
            isEllipsisFeatureEnabled,
        ]);
        // Update text overflow state if the element size changes for any reason/s.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useResizeObserver(contentRef, () => {
            checkIfTextContentIsOverflowing();
        });
        /**
         * Update text overflow state if certain values change that could affect the
         *   element's dimensions or the evaluation logic itself.
         */
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            checkIfTextContentIsOverflowing();
        }, [checkIfTextContentIsOverflowing]);
    }
    const callbackRef = useCallback((node) => {
        if (contentRef === null && node !== null) {
            setContentRef(node);
        }
    }, 
    /**
     * We need to set the ref only once when the ref is ready, hence
     *   disabling the 'missing dependency' lint warning.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const { style, type } = defaultVariantMapping[variant];
    const variantStyle = cx(styles[style]);
    const currentTag = overrideTag || type;
    const props = {
        ref: callbackRef,
        className: cx(variantStyle, styles[color], additionalClass, {
            [styles.whiteSpace]: preserveWhitespace,
            [styles.noMargin]: noMargin || isEllipsisFeatureEnabled,
            [styles.wrapContent]: wrapContent,
            /**
             * We no longer apply ellipsis CSS conditionally.
             * But if that ever needs to be done again after manually detecting
             *   overflow or for any other condition,
             *   refer to the scenario below and ensure a base class is added
             *   that from the first render adds `display: -webkit-box;` to
             *   the element.
             * Adding that style dynamically causes `ResizeObserver` to not work well.
             *
             * Ideally, it's always better to avoid applying ellipsis conditionally.
             *
             * ---
             *
             * ResizeObserver needs the above style to work properly in some edge
             *   cases.
             *
             * E.g.,
             * 1. Comment the line after this comment.
             * 2. Open answers page with data panel v2.
             * 3. Ensure there's a column name with ellipsis.
             * 4. Increase panel width such that ellipsis is gone.
             * 5. Decrease width again to expect ellipsis again.
             * Instead, the text wraps because ResizeObserver is no longer
             *   watching the element.
             *
             * Why does this happen ??
             * - Not sure entirely, but probably related to this element not
             *     having any proper box sizing/layout CSS.
             */
            /**
             * Different classes are needed for single and multi line ellipsis.
             * Line clamp = 1 doesn't work well for some cases like long words
             *   with no spaces or breaking points.
             *
             * Further, line clamp CSS is force-applied for when `children` contain
             *   non-text items.
             *   - With such items, ellipsis CSS doesn't work.
             *   - And line clamp misses an edge case (detailed above) but it is
             *       the best option for such content.
             */
            [styles.ellipsisOnFirstLine]: !doChildrenContainNonTextValues && isEllipsisOnFirstLine,
            [styles.ellipsisMultiLine]: isEllipsisFeatureEnabled &&
                (!isEllipsisOnFirstLine || doChildrenContainNonTextValues),
        }),
        style: isEllipsisFeatureEnabled &&
            (!isEllipsisOnFirstLine || doChildrenContainNonTextValues)
            ? { WebkitLineClamp: ellipsis.rows, ...inlineStyle }
            : { ...inlineStyle },
        'data-testid': dataTestId,
        ...getTooltipProps(children, tooltipConfig, isTextContentOverflowing),
        ...getAriaProps(role),
        ...restProps,
    };
    const element = React.createElement(currentTag, props, children);
    return element;
};
//# sourceMappingURL=typography.js.map